import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatCalendarDate, formatTime } from '@/lib/dates';

export type BracketGame = {
  id: string; label?: string; round: number; slot?: number; bracket_id?: string;
  game_type?: string; game_number?: number; date?: string; start?: string;
  court?: string; venue?: string; note?: string;
  team_a_id?: string; team_b_id?: string; team_a?: string; team_b?: string;
  seed_a?: string; seed_b?: string; score_a?: number | null; score_b?: number | null;
  winner_id?: string; next_game_id?: string; next_game_slot?: number;
  is_championship?: boolean;
};
type Round = { round: number; games: BracketGame[]; notes?: string[] };

export function bracketCenters(games: BracketGame[], heights: Record<string, number>) {
  const byId = new Map(games.map(g => [g.id, g]));
  const incoming = new Map<string, BracketGame[]>();
  for (const g of games) {
    if (!g.next_game_id || !byId.has(g.next_game_id) || ![1, 2].includes(g.next_game_slot ?? 0)) continue;
    incoming.set(g.next_game_id, [...(incoming.get(g.next_game_id) ?? []), g]);
  }
  const centers: Record<string, number> = {};
  const visiting = new Set<string>();
  let cursor = 0;
  const height = (g: BracketGame) => heights[g.id] ?? 180;
  const visit = (g: BracketGame): number => {
    if (centers[g.id] !== undefined) return centers[g.id];
    if (visiting.has(g.id)) return cursor;
    visiting.add(g.id);
    const feeders = (incoming.get(g.id) ?? []).sort((a, b) => (a.next_game_slot ?? 0) - (b.next_game_slot ?? 0));
    if (feeders.length) {
      const values = feeders.map(visit);
      centers[g.id] = values.reduce((a, b) => a + b, 0) / values.length;
    } else {
      centers[g.id] = cursor + height(g) / 2;
      cursor += height(g) + 32;
    }
    visiting.delete(g.id);
    return centers[g.id];
  };
  // Traverse from finals so leaf ordering follows the actual top/bottom slots.
  const ordered = [...games].sort((a, b) => a.round - b.round || (a.slot ?? 0) - (b.slot ?? 0));
  ordered.filter(g => !g.next_game_id || !byId.has(g.next_game_id)).forEach(visit);
  ordered.forEach(visit);
  const top = Math.min(0, ...games.map(g => centers[g.id] - height(g) / 2));
  for (const g of games) centers[g.id] -= top;
  return { centers, height: Math.max(0, ...games.map(g => centers[g.id] + height(g) / 2)) + 8 };
}

export function bracketRoundTitle(round: Round) {
  if (round.games.some(g => g.is_championship)) return 'Championship';
  const label = (round.games[0]?.label ?? `Round ${round.round}`).replace(/\s+\d+$/, '').trim();
  if (/^semifinals?$/i.test(label)) return 'Semifinals';
  if (/^quarterfinals?$/i.test(label)) return 'Quarterfinals';
  return label;
}

function TeamRow({ slug, id, name, seed, score, winner, loser }: {
  slug: string; id?: string; name?: string; seed?: string; score?: number | null;
  winner?: boolean; loser?: boolean;
}) {
  return <div className={`flex min-h-11 items-center gap-2 px-2 py-2 text-sm ${winner ? 'bg-primary font-bold text-primary-foreground' : loser ? 'text-muted-foreground' : 'text-foreground'}`}>
    <span className="flex h-6 w-8 shrink-0 items-center justify-center rounded border border-current/20 text-[11px] tabular-nums">{seed ?? ''}</span>
    <span className="min-w-0 flex-1 break-words">
      {id ? <Link className="underline-offset-2 hover:underline" to={`/schedule/${slug}/team/${id}`}>{name || 'TBD'}</Link> : name || 'TBD'}
    </span>
    {score != null && <span className="shrink-0 tabular-nums">{score}</span>}
  </div>;
}

export default function PublicDivisionBracket({ rounds, slug, borderColor, consolation = false }: {
  rounds: Round[]; slug: string; borderColor: string; consolation?: boolean;
}) {
  const columns = useMemo(() => rounds.filter(r => r.games.length).sort((a, b) => a.round - b.round), [rounds]);
  const games = useMemo(() => columns.flatMap(r => r.games), [columns]);
  const finals = games.filter(g => g.is_championship && !consolation);
  const count = columns.length + (finals.length ? 1 : 0);
  const root = useRef<HTMLDivElement>(null);
  const [heights, setHeights] = useState<Record<string, number>>({});
  const [lines, setLines] = useState<string[]>([]);
  const layout = useMemo(() => bracketCenters(games, heights), [games, heights]);
  const template = { gridTemplateColumns: `repeat(${count}, minmax(200px, 1fr))`, minWidth: count * 200 + Math.max(0, count - 1) * 32 };

  useLayoutEffect(() => {
    const node = root.current;
    if (!node) return;
    const measure = () => {
      const measured: Record<string, number> = {};
      node.querySelectorAll<HTMLElement>('[data-bracket-game]').forEach(card => {
        measured[card.dataset.bracketGame ?? ''] = card.offsetHeight;
      });
      setHeights(old => JSON.stringify(old) === JSON.stringify(measured) ? old : measured);
      const rect = node.getBoundingClientRect();
      const paths: string[] = [];
      const cards = new Map(Array.from(node.querySelectorAll<HTMLElement>('[data-bracket-game]')).map(card => [card.dataset.bracketGame, card]));
      for (const g of games) {
        const source = cards.get(g.id);
        const target = g.next_game_id ? cards.get(g.next_game_id) : undefined;
        const slot = target?.querySelector<HTMLElement>(`[data-team-slot="${g.next_game_slot}"]`);
        if (!source || !target || !slot || ![1, 2].includes(g.next_game_slot ?? 0)) continue;
        const a = source.getBoundingClientRect(), b = slot.getBoundingClientRect();
        const x1 = a.right - rect.left, y1 = a.top + a.height / 2 - rect.top;
        const x2 = b.left - rect.left, y2 = b.top + b.height / 2 - rect.top;
        const middle = x2 - 16;
        paths.push(`M ${x1} ${y1} H ${middle} V ${y2} H ${x2}`);
      }
      for (const g of finals) {
        const source = cards.get(g.id);
        const champion = Array.from(node.querySelectorAll<HTMLElement>('[data-champion]')).find(el => el.dataset.champion === g.id);
        if (!source || !champion) continue;
        const a = source.getBoundingClientRect(), b = champion.getBoundingClientRect();
        paths.push(`M ${a.right - rect.left} ${a.top + a.height / 2 - rect.top} H ${b.left - rect.left}`);
      }
      setLines(old => JSON.stringify(old) === JSON.stringify(paths) ? old : paths);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    node.querySelectorAll('[data-bracket-game]').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [games, layout, finals.map(g => g.id).join(',')]);

  if (!games.length) return null;
  return <div className="max-w-full overflow-x-auto pb-3" aria-label={consolation ? 'Consolation bracket' : 'Tournament bracket'}>
    <div className="grid gap-x-8 pb-4" style={template}>
      {columns.map(r => <div key={r.round}>
        <h3 className="text-sm font-bold text-foreground">{bracketRoundTitle(r)}</h3>
        {(r.notes ?? []).map((note, i) => <p key={i} className="mt-2 whitespace-pre-wrap text-xs text-muted-foreground">{note}</p>)}
      </div>)}
      {!!finals.length && <h3 className="text-sm font-bold text-foreground">Champion</h3>}
    </div>
    <div ref={root} className="relative grid gap-x-8" style={{ ...template, height: layout.height }}>
      <svg className="pointer-events-none absolute inset-0 h-full w-full overflow-visible text-border" aria-hidden="true">
        {lines.map((path, i) => <path key={i} d={path} fill="none" stroke="currentColor" strokeWidth="2" />)}
      </svg>
      {columns.map(r => <div key={r.round} className="relative min-w-0">
        {r.games.map(g => {
          const aWins = !!g.winner_id && g.winner_id === g.team_a_id;
          const bWins = !!g.winner_id && g.winner_id === g.team_b_id;
          const meta = [g.game_number != null ? `Game ${g.game_number}` : null,
            [g.date ? formatCalendarDate(g.date, 'MMM d') : null, g.start ? formatTime(g.start) : null].filter(Boolean).join(' '), g.court, g.venue].filter(Boolean).join(' · ');
          return <article key={g.id} data-bracket-game={g.id} className={`absolute w-full overflow-hidden rounded-md border bg-card shadow-sm ${g.is_championship ? 'ring-2 ring-primary' : ''}`}
            style={{ top: layout.centers[g.id] - (heights[g.id] ?? 180) / 2, borderLeft: `4px solid ${borderColor}` }}>
            <div className="border-b px-2 py-1.5 text-xs font-semibold text-muted-foreground">{g.is_championship ? 'Championship' : g.label || `Game ${g.game_number ?? g.slot ?? ''}`}</div>
            <div data-team-slot="1"><TeamRow slug={slug} id={g.team_a_id} name={g.team_a} seed={g.seed_a} score={g.score_a} winner={aWins} loser={bWins} /></div>
            <div data-team-slot="2" className="border-t"><TeamRow slug={slug} id={g.team_b_id} name={g.team_b} seed={g.seed_b} score={g.score_b} winner={bWins} loser={aWins} /></div>
            {meta && <p className="border-t px-2 py-2 text-[11px] leading-relaxed text-muted-foreground">{meta}</p>}
            {g.note && <p className="whitespace-pre-wrap border-t px-2 py-2 text-xs text-muted-foreground">{g.note}</p>}
          </article>;
        })}
      </div>)}
      {!!finals.length && <div className="relative min-w-0">{finals.map(g => {
        const winnerA = !!g.winner_id && g.winner_id === g.team_a_id;
        const winnerB = !!g.winner_id && g.winner_id === g.team_b_id;
        return <div key={g.id} data-champion={g.id} className="absolute w-full -translate-y-1/2 overflow-hidden rounded-md border-2 border-primary bg-card shadow-sm" style={{ top: layout.centers[g.id] }}>
          <TeamRow slug={slug} id={winnerA ? g.team_a_id : winnerB ? g.team_b_id : undefined} name={winnerA ? g.team_a : winnerB ? g.team_b : 'TBD'} seed={winnerA ? g.seed_a : winnerB ? g.seed_b : undefined} winner={winnerA || winnerB} />
        </div>;
      })}</div>}
    </div>
  </div>;
}